/* eslint-disable office-addins/no-context-sync-in-loop */
/* global Word console */

import { RemoteAction } from "../remoteActions";
import { getCurrentDocumentPath } from "../word";
import { handleAddElement } from "./operations/add";
import { handleRemoveElement } from "./operations/remove";
import { handleUpdate } from "./operations/update";
import { saveDocument } from "./save";
import { CanvasChange, isDocumentElement, ParseState } from "./types";

async function applyDocumentChanges(changes: CanvasChange[], context: Word.RequestContext, frontendBaseUrl?: string) {
  const state: ParseState = {
    createdElements: [],
    frontendBaseUrl,
    listState: {}, // Initialize list state tracking (like Python's list_state dict)
  };

  // Load document and enable change tracking
  context.load(context.document.body.paragraphs, "items");
  context.load(context.document, "changeTrackingMode");
  await context.sync();

  // Enable change tracking to create tracked changes
  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  console.log("Enabled change tracking");
  await context.sync();

  let allElementsInDocument = context.document.body.paragraphs.items;
  if (allElementsInDocument.length === 0) {
    console.warn("Document is empty, cannot apply changes");
    return;
  }

  // Track indices that have been added/removed to adjust target indices
  const addedIndices: number[] = [];

  let lastInsertedParagraph: Word.Paragraph | null = null;
  let lastOriginalIndex: number | null = null;

  // Process each change in order
  for (let change of changes) {
    // Reload document content to get current state (like Python does)
    context.load(context.document.body.paragraphs, "items");
    await context.sync();
    allElementsInDocument = context.document.body.paragraphs.items;

    const originalTargetIndex = change.old_word_index;

    // Skip changes with null/undefined old_word_index (matching Python behavior)
    if (originalTargetIndex === null) {
      continue;
    }

    // Find target paragraph with index adjustment (matching Python logic exactly)
    let targetParagraph: Word.Paragraph | null = null;
    let insertAfter = false;

    // Adjust index based on previous additions/removals (using <= like Python)
    const addedBefore = addedIndices.filter((i) => i <= originalTargetIndex).length;
    let adjustedIndex = originalTargetIndex + addedBefore;

    if (
      change.op === "+" &&
      lastInsertedParagraph &&
      lastOriginalIndex !== null &&
      originalTargetIndex === lastOriginalIndex
    ) {
      // Same old_word_index as previous add - insert after the last added element
      targetParagraph = lastInsertedParagraph;
      insertAfter = true;
    } else if (adjustedIndex >= allElementsInDocument.length) {
      // Out of bounds - append at end (matching Python's add_after = True logic)
      if (allElementsInDocument.length > 0) {
        targetParagraph = allElementsInDocument[allElementsInDocument.length - 1];
        insertAfter = true;
      }
    } else {
      // Use adjusted index - insert before to start a new sequence
      adjustedIndex = Math.max(0, adjustedIndex);
      if (adjustedIndex < allElementsInDocument.length) {
        targetParagraph = allElementsInDocument[adjustedIndex];
        insertAfter = false;
      }
    }

    if (!targetParagraph) continue;

    // Apply the change
    switch (change.op) {
      case "+": {
        let currentTarget = targetParagraph;
        let lastCreated: Word.Paragraph | null = null;
        const newEl = change.e2;
        if (isDocumentElement(newEl)) {
          // For multiple insertions: after first, insert after the previously created element
          const created = await handleAddElement(
            currentTarget,
            context.document,
            newEl,
            state,
            insertAfter,
            allElementsInDocument,
            context
          );
          lastCreated = created;
          // Use the newly created paragraph as target for next insertion
          currentTarget = created;
          // Track the last inserted paragraph for ordering consecutive additions
          if (lastCreated) {
            lastInsertedParagraph = lastCreated;
            lastOriginalIndex = originalTargetIndex;
          }
          addedIndices.push(originalTargetIndex);
        }
        break;
      }
      case "-":
        await handleRemoveElement(targetParagraph);
        // Reset tracking since document structure changed significantly
        lastInsertedParagraph = null;
        lastOriginalIndex = null;
        break;
      case "~":
        await handleUpdate(change, context, targetParagraph, allElementsInDocument, state);
        lastInsertedParagraph = null;
        lastOriginalIndex = null;
        break;
    }

    // Sync after each change
    await context.sync();
  }

  // Disable track changes after all changes have been applied
  context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
  await context.sync();
}

export const changeAction: RemoteAction = {
  name: "canvasChanges",
  onExecute: async (data: any) => {
    const changes: CanvasChange[] = data.changes;
    console.log(changes);

    try {
      // Get document path to determine frontend base URL (using same logic as App.tsx)
      let frontendBaseUrl: string | undefined;
      try {
        const documentPath = await getCurrentDocumentPath();
        const apiUrls = {
          local: "http://localhost:4200",
          dev: "https://dev.silvernova.ai",
          prod: "https://app.silvernova.ai",
        };

        if (documentPath.startsWith("http://localhost:1337/")) {
          frontendBaseUrl = apiUrls.local;
        } else if (
          documentPath.startsWith("https://api.associate-dev.silberfluss.io/") ||
          documentPath.startsWith("https://api.dev.silvernova.ai/")
        ) {
          frontendBaseUrl = apiUrls.dev;
        } else if (
          documentPath.startsWith("https://api.associate.silberfluss.io/") ||
          documentPath.startsWith("https://api.silvernova.ai/")
        ) {
          frontendBaseUrl = apiUrls.prod;
        } else {
          frontendBaseUrl = apiUrls.prod; // Default to prod if unknown
        }
      } catch (error) {
        console.warn("Could not get document path for frontend base URL:", error);
        // Continue without frontend base URL - links will be relative
      }

      await Word.run(async (context) => {
        await applyDocumentChanges(changes, context, frontendBaseUrl);
        await context.sync();
      });

      // Save the document so further changes are in relation to the current state
      await saveDocument();

      return { success: true };
    } catch (error: any) {
      console.error("Error applying changes to Word:", error);
      return { success: false, error: error.message || String(error) };
    }
  },
};
